"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { useSession, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import apiClient from '@/libs/api';
import { trackFunnel, track } from '@/libs/analytics';

// Type definitions for the credits context
interface CreditsContextType {
  credits: number | null;
  uqf: number | null; // User quality flag: 1 = free user, 2 = paid user
  subscriptionStatus: string | null; // Stripe subscription status ("active", "canceled", etc.)
  isGuest: boolean; // Whether user is browsing as a guest (not authenticated)
  isLoading: boolean;
  isRefreshing: boolean;
  checkCredits: (amount: number) => Promise<boolean>;
  deductCredits: (params: DeductCreditsParams) => Promise<boolean>;
  refreshCredits: () => Promise<void>;
  error: string | null;
}

// Parameters for the deduct credits operation
interface DeductCreditsParams {
  amount: number;
  toolUsed: string;
}

// Create the context
const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

// Provider component that wraps your app and makes credits available
export function CreditsProvider({ children }: { children: ReactNode }) {
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const [credits, setCredits] = useState<number | null>(null);
  const [uqf, setUqf] = useState<number | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user credits using the /api/user/me endpoint
  const fetchUserCredits = async (isInitialLoad: boolean = true) => {
    if (!session?.user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // Only set isLoading for initial loads, use isRefreshing for background refreshes
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const response = await apiClient.get("/user/me");
      // TypeScript needs explicit type assertion here
      const data = response as unknown as {
        credits: number;
        uqf: number;
        subscription?: { status?: string | null };
      };
      setCredits(data?.credits ?? 0);
      setUqf(data?.uqf ?? 1);
      setSubscriptionStatus(data?.subscription?.status ?? null);
    } catch (error: any) {
      console.error("Error fetching user credits:", error);

      // If user not found (deleted), log them out
      if (error?.response?.status === 404) {
        console.log("User not found, logging out...");
        await signOut({ redirect: false });
        router.push("/");
      } else {
        setError(error?.message || 'Failed to fetch credits');
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Public method to refresh credits (background refresh, doesn't show loading spinner)
  const refreshCredits = async () => {
    await fetchUserCredits(false);
  };

  // Check if user has enough credits
  const checkCredits = async (amount: number): Promise<boolean> => {
    setError(null);

    try {
      const response = await apiClient.post('/credits/check', { amount });
      // TypeScript needs explicit type assertion here
      const data = response as unknown as { hasCredits: boolean };
      const hasEnough = data?.hasCredits === true;

      // Track credit check
      if (!hasEnough) {
        trackFunnel('monetization', 'insufficient_credits', {
          credits_needed: amount,
          user_credits: credits,
          deficit: amount - (credits || 0)
        });
      }

      return hasEnough;
    } catch (err) {
      setError(err.message || 'Error checking credits');
      return false;
    }
  };

  // Deduct credits for tool usage
  const deductCredits = async ({
    amount,
    toolUsed,
  }: DeductCreditsParams): Promise<boolean> => {
    setError(null);

    try {
      const response = await apiClient.post('/credits/deduct', {
        amount,
        toolUsed,
      });

      // TypeScript needs explicit type assertion here
      const data = response as unknown as { success: boolean };

      // Track tool usage
      if (data?.success === true) {
        track('tool_usage', 'tool_engagement', {
          tool_id: toolUsed,
          action: 'executed',
          credits_used: amount,
          success: true
        });

        trackFunnel('tool_engagement', 'tool_executed', {
          tool_id: toolUsed,
          credits_used: amount,
          remaining_credits: (credits || 0) - amount
        });

        // If successful, refresh credits to update the UI (background refresh)
        await fetchUserCredits(false);
      } else {
        track('tool_usage', 'tool_engagement', {
          tool_id: toolUsed,
          action: 'failed',
          credits_used: 0,
          success: false
        });
      }

      return data?.success === true;
    } catch (err) {
      track('tool_usage', 'tool_engagement', {
        tool_id: toolUsed,
        action: 'error',
        credits_used: 0,
        success: false
      });

      if (err.message === 'Insufficient credits') {
        setError('You don\'t have enough credits for this operation');
      } else {
        setError(err.message || 'Error deducting credits');
      }
      return false;
    }
  };

  // Load credits when session becomes available or user changes
  useEffect(() => {
    if (sessionStatus === 'loading') {
      setIsLoading(true);
      return;
    }

    if (sessionStatus === 'unauthenticated') {
      // Guest mode - no credits, not loading
      setCredits(0);
      setUqf(null);
      setSubscriptionStatus(null);
      setIsGuest(true);
      setIsLoading(false);
      return;
    }

    // Session authenticated - fetch credits if we don't have them or user changed
    if (session?.user?.id) {
      setIsGuest(false);
      if (credits === null) {
        fetchUserCredits();
      } else {
        // Already have credits, not loading
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [session?.user?.id, sessionStatus]);

  // Context value
  const value = {
    credits,
    uqf,
    subscriptionStatus,
    isGuest,
    isLoading,
    isRefreshing,
    error,
    checkCredits,
    deductCredits,
    refreshCredits,
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

// Hook to use the credits context
export function useCreditsContext() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCreditsContext must be used within a CreditsProvider');
  }
  return context;
}
